<template>
  <q-table
    v-bind="$attrs"
    :data="tableData"
    wrap-cells
    hide-bottom
    :grid="mobile"
    :columns="columns"
    :rows-per-page-options="[0]"
    :pagination="{
      page: 1,
      rowsPerPage: 0, // 0 means all rows
    }"
    flat
    :hide-header="hideHeader"
    class="cmp-domains-result-table ws-table remove-scroll"
    :class="{
      mobile,
      'not-mobile': desktop || tablet,
      'hide-header': hideHeader,
      dark,
    }"
  >
    <template v-slot:header="props">
      <q-tr :props="props">
        <q-th key="domain" :props="props" class="col-th-domain">
          {{ $t('domain_checker.domain') }}
        </q-th>

        <q-th key="period" auto-width :props="props" class="col-th-period">
          {{ $t('cart.col.period.title') }}
        </q-th>

        <q-th key="price" :props="props" class="col-th-price text-right">
          {{ $t('domain_checker.price') }}
        </q-th>

        <q-th
          key="action"
          :props="props"
          class="col-th-action text-bold ws-text-dark"
        />
      </q-tr>
    </template>

    <template v-slot:body="props">
      <q-tr class="spacer" />
      <domain-result-item :props="props" :dark="dark" />
      <q-tr
        v-if="isShownBundleForDomain(get(props, 'row.domain.name'))"
        class="spacer"
      />
      <domain-result-item
        v-if="isShownBundleForDomain(get(props, 'row.domain.name'))"
        is-bundle
        :props="props"
        :dark="dark"
      />
    </template>

    <!-- GRID TEMPLATE (MOBILE) -->
    <template v-slot:item="props">
      <domain-result-item mobile :props="props" :dark="dark" />
      <domain-result-item
        v-if="isShownBundleForDomain(get(props, 'row.domain.name'))"
        is-bundle
        :props="props"
        :dark="dark"
        mobile
      />
    </template>
  </q-table>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { type VueConstructor } from 'vue';
import { mapGetters } from 'vuex';
import { QTable, QTr, QTh } from 'quasar';

import { getBundles } from '@/services/domain-checker.service';
import type { FindDomainInCartGetter } from '@/store/getters.d';
import type { Domain, DomainBundle } from '../dc.types';
import DomainResultItem from './DomainResultItem.vue';

@Component({
  components: {
    DomainResultItem,
    // fix ref: https://github.com/quasarframework/quasar/issues/6277#issuecomment-652391838
    QTable: (QTable as unknown) as VueConstructor<Vue>,
    QTr,
    QTh,
  },
  computed: {
    ...mapGetters(['dcFindDomainInCart']),
  },
})
export default class DomainsResultTable extends Vue {
  @Prop({ required: true })
  readonly domains!: Domain[];
  @Prop({ default: () => [] })
  readonly showBundleForDomains!: string[];
  @Prop(Boolean)
  readonly hideHeader!: boolean;
  @Prop(Boolean)
  readonly dark!: boolean;

  // Vuex
  dcFindDomainInCart!: FindDomainInCartGetter;

  domainBundles: DomainBundle[] = [];
  columns = [
    {
      name: 'domain',
      align: 'left',
    },
    {
      name: 'period',
      align: 'left',
    },
    {
      name: 'price',
      align: 'left',
    },
    {
      name: 'action',
      align: 'right',
    },
  ];

  mounted() {
    if (this.showBundleForDomains.length) {
      getBundles(this.showBundleForDomains).then(bundles => {
        this.domainBundles = bundles;
      });
    }
  }

  get tableData() {
    return Array.from(this.domains, (domain: Domain) => {
      return {
        domain,
        bundle: this.getBundleFor(domain.name),
      };
    });
  }

  getBundleFor(domainName: string): DomainBundle | undefined {
    return this.domainBundles.find(
      (bundle: DomainBundle) => bundle.mainDomain.name === domainName
    );
  }

  isShownBundleForDomain(domainName: string): boolean {
    const bundle = this.showBundleForDomains.includes(domainName)
      ? this.getBundleFor(domainName)
      : undefined;

    return !!(
      bundle &&
      // TODO: fix on backend, endpoint should return only free domains without errors
      bundle.mainDomain.availableForPurchase &&
      bundle.discountedDomain.availableForPurchase &&
      !bundle.mainDomain.error &&
      !bundle.discountedDomain.error
    );
  }
}
</script>

<style lang="scss" scoped>
.cmp-domains-result-table.ws-table {
  background: transparent;

  &.mobile {
    border: none;
    border-radius: 0;
    padding: 0;

    ::v-deep .q-table__middle {
      display: none;
    }
  }

  &.not-mobile {
    ::v-deep {
      table.q-table {
        width: 100%;
      }

      thead .q-tr {
        position: relative;

        th {
          color: $ws-text-color-dark;
          font-weight: 600;
          font-size: 16px;
          line-height: 18px;
          border: none;
          background: none;
        }
      }

      thead th,
      tbody .q-td {
        &:first-of-type {
          padding-left: map-get($space-xl, x);
        }
      }

      thead,
      tbody {
        .col-th-domain,
        .td-domain {
          width: 100%;
        }
        .col-th-period,
        .td-period {
          min-width: 80px;
        }
        .col-th-price,
        .td-price {
          min-width: 190px;
        }
        .col-th-action,
        .td-action {
          width: 1%;
          white-space: nowrap;
        }
      }

      tbody tr.spacer {
        height: 4px;
        border: none;
        background: none;

        &:first-of-type {
          display: none;
        }
      }
    }

    &.without-header ::v-deep tbody tr:first-of-type .q-td {
      border-top: $ws-border-thick;
    }
  }
}
</style>
